/**
 * Typed wrappers around the membership + allocation cell endpoints. Members
 * and cells share the same row in `members` (the JSON `allocations` column),
 * so a single module covers both.
 *
 * On any non-OK response, surface a toast via `notifyToast` and re-throw
 * the typed error so callers can react.
 */
import type { AllocationCell, Member, ProjectActualCellResponse, YmKey } from "@shared/types";
import * as Toast from "../components/Toast";
import type { PersonProjectAllocationRow } from "../data";
import { apiFetch } from "./api";

export interface MembershipsApiError extends Error {
  status: number;
  code?: string;
  body: unknown;
}

async function decodeError(res: Response, fallback: string): Promise<MembershipsApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // not json
  }
  const code =
    body && typeof body === "object" && "code" in body
      ? String((body as { code?: unknown }).code)
      : undefined;
  const message =
    body && typeof body === "object" && "message" in body
      ? String((body as { message?: unknown }).message)
      : fallback;
  const err = new Error(message) as MembershipsApiError;
  err.status = res.status;
  err.body = body;
  if (code !== undefined) err.code = code;
  return err;
}

async function expect<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const err = await decodeError(res, fallback);
    Toast.notifyToast(err.message);
    throw err;
  }
  return (await res.json()) as T;
}

async function expectNoContent(res: Response, fallback: string): Promise<void> {
  if (res.status === 204) return;
  if (!res.ok) {
    const err = await decodeError(res, fallback);
    Toast.notifyToast(err.message);
    throw err;
  }
}

export async function addProjectMember(projectId: number): Promise<Member> {
  const res = await apiFetch(`/api/projects/${projectId}/members`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: "{}",
  });
  return expect<Member>(res, "could not add member");
}

export interface MemberPatchInput {
  pid?: number;
  role?: string;
}

export async function patchProjectMember(
  projectId: number,
  memberId: number,
  patch: MemberPatchInput,
): Promise<Member> {
  const res = await apiFetch(`/api/projects/${projectId}/members/${memberId}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  return expect<Member>(res, "could not update member");
}

export async function deleteProjectMember(projectId: number, memberId: number): Promise<void> {
  const res = await apiFetch(`/api/projects/${projectId}/members/${memberId}`, {
    method: "DELETE",
  });
  return expectNoContent(res, "could not remove member");
}

export async function addPersonMembership(personId: number, projectId: number): Promise<Member> {
  const res = await apiFetch(`/api/people/${personId}/memberships`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ projectId }),
  });
  return expect<Member>(res, "could not add membership");
}

export async function putAllocationCell(
  projectId: number,
  memberId: number,
  ymKey: YmKey,
  cell: AllocationCell,
): Promise<{ value: number }> {
  const res = await apiFetch(
    `/api/projects/${projectId}/members/${memberId}/allocations/${ymKey}`,
    {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(cell),
    },
  );
  return expect<{ value: number }>(res, "could not save allocation");
}

export async function putActualCell(
  projectId: number,
  personId: number,
  ymKey: YmKey,
  hours: number,
): Promise<ProjectActualCellResponse> {
  const res = await apiFetch(`/api/projects/${projectId}/actuals/${personId}/${ymKey}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ hours }),
  });
  return expect<ProjectActualCellResponse>(res, "could not save actual hours");
}

export async function deleteActualCell(
  projectId: number,
  personId: number,
  ymKey: YmKey,
): Promise<void> {
  const res = await apiFetch(`/api/projects/${projectId}/actuals/${personId}/${ymKey}`, {
    method: "DELETE",
  });
  return expectNoContent(res, "could not clear actual hours");
}

export async function deleteAllocationCell(
  projectId: number,
  memberId: number,
  ymKey: YmKey,
): Promise<void> {
  const res = await apiFetch(
    `/api/projects/${projectId}/members/${memberId}/allocations/${ymKey}`,
    { method: "DELETE" },
  );
  return expectNoContent(res, "could not clear allocation");
}

export async function getPersonAllocations(
  personId: number,
): Promise<PersonProjectAllocationRow[]> {
  const res = await apiFetch(`/api/people/${personId}/allocations`);
  return expect<PersonProjectAllocationRow[]>(res, "could not load person allocations");
}
